import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch } from 'react-redux';
import { useLocation } from "react-router";
import { fetchEnd, fetchStart, useNotify } from "react-admin";
import { apiUrl } from "../../dataProvider";

const buildMetricTrend = (metricData, mapper) => {
    if (!metricData) return undefined;
    return Object.keys(metricData).map(date => mapper(date, metricData[date]));
};

function GetAWSMetrics() {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const notify = useNotify();
    const { search } = useLocation();

    useEffect(() => {
        let isMounted = true;

        const fetchSiteMetrics = async (dates) => {
            setLoading(true);
            dispatch(fetchStart());

            try {
                const { data } = await axios.get(`${apiUrl}/aws/metrics${dates}`);
                if (isMounted) {
                    setMetrics(data);
                }
            } catch (error) {
                const response = error?.response;
                const message = response?.statusText;
                const status = response?.status;
                if (status === 401 || status === 403) {
                    window.location.replace('/#/login');
                }
                if (message) {
                    notify(message, 'warning');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
                dispatch(fetchEnd());
            }
        };

        fetchSiteMetrics(search || '');

        return () => {
            isMounted = false;
        };
    }, [dispatch, notify, search]);

    const latencyTrend = useMemo(
        () => buildMetricTrend(metrics?.applicationLatencyPXX, (date, entry) => ({
            date,
            P10: entry.P10,
            P50: entry.P50,
            P75: entry.P75,
            P85: entry.P85,
            P90: entry.P90,
            P95: entry.P95,
            P99: entry.P99,
            P999: entry.P999,
        })),
        [metrics],
    );

    const envHealthTrend = useMemo(
        () => buildMetricTrend(metrics?.environmentHealth, (date, health) => ({
            date,
            health,
        })),
        [metrics],
    );

    const totalRequestsTrend = useMemo(
        () => buildMetricTrend(metrics?.totalRequests, (date, requests) => ({
            date,
            requests,
        })),
        [metrics],
    );

    const okInstanceTrend = useMemo(
        () => buildMetricTrend(metrics?.okInstance, (date, okInstance) => ({
            date,
            okInstance,
        })),
        [metrics],
    );

    const degradedInstanceTrend = useMemo(
        () => buildMetricTrend(metrics?.degeradedInstance, (date, degradedInstance) => ({
            date,
            degradedInstance,
        })),
        [metrics],
    );

    const nodataInstanceTrend = useMemo(
        () => buildMetricTrend(metrics?.nodataInstance, (date, nodataInstance) => ({
            date,
            nodataInstance,
        })),
        [metrics],
    );

    const pendingInstanceTrend = useMemo(
        () => buildMetricTrend(metrics?.pendingInstance, (date, pendingInstance) => ({
            date,
            pendingInstance,
        })),
        [metrics],
    );

    const severeInstanceTrend = useMemo(
        () => buildMetricTrend(metrics?.severeInstance, (date, severeInstance) => ({
            date,
            severeInstance,
        })),
        [metrics],
    );

    const unknownInstanceTrend = useMemo(
        () => buildMetricTrend(metrics?.unknownInstance, (date, unknownInstance) => ({
            date,
            unknownInstance,
        })),
        [metrics],
    );

    const warningInstanceTrend = useMemo(
        () => buildMetricTrend(metrics?.warningInstance, (date, warningInstance) => ({
            date,
            warningInstance,
        })),
        [metrics],
    );

    return {
        metrics,
        latencyTrend,
        envHealth: envHealthTrend,
        totalRequestsTrend,
        severeInstanceTrend,
        warningInstanceTrend,
        degradedInstanceTrend,
        okInstanceTrend,
        pendingInstanceTrend,
        unknownInstanceTrend,
        nodataInstanceTrend,
        loading,
    };
}

export default GetAWSMetrics;
